import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronRight } from 'lucide-react'
import type { KlasserRow } from './api'
import { useBruktbilFilters } from '../useBruktbilFilters'
import { klasseDetaljerUrl } from '../detaljerUrl'

type SortCol = 'count' | 'share_pct' | 'finans_andel_pct' | 'avg_bilalder' | 'avg_kjoper_alder'
type Sort = { col: SortCol; dir: 'asc' | 'desc' } | null

interface Props { rows: KlasserRow[] }

function SortIcon({ col, sort }: { col: SortCol; sort: Sort }) {
  if (!sort || sort.col !== col) return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  return sort.dir === 'asc'
    ? <ArrowUp className="w-4 h-4 inline ml-1" />
    : <ArrowDown className="w-4 h-4 inline ml-1" />
}

export default function KlasserTable({ rows }: Props) {
  const { t } = useTranslation('bruktbil')
  const filters = useBruktbilFilters()
  const navigate = useNavigate()
  const [sort, setSort] = useState<Sort>(null)

  function handleSort(col: SortCol) {
    setSort(s => s?.col === col ? (s.dir === 'asc' ? { col, dir: 'desc' } : null) : { col, dir: 'asc' })
  }

  const sorted = sort
    ? [...rows].sort((a, b) => {
        const va = sort.col === 'count' ? a.count
          : sort.col === 'share_pct' ? a.share_pct
          : sort.col === 'finans_andel_pct' ? a.finans_andel_pct
          : sort.col === 'avg_bilalder' ? (a.avg_bilalder ?? -1)
          : (a.avg_kjoper_alder ?? -1)
        const vb = sort.col === 'count' ? b.count
          : sort.col === 'share_pct' ? b.share_pct
          : sort.col === 'finans_andel_pct' ? b.finans_andel_pct
          : sort.col === 'avg_bilalder' ? (b.avg_bilalder ?? -1)
          : (b.avg_kjoper_alder ?? -1)
        return sort.dir === 'asc' ? va - vb : vb - va
      })
    : rows

  const totalCount = rows.reduce((s, r) => s + r.count, 0)

  return (
    <div className="card p-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          {sort && (
            <button onClick={() => setSort(null)} className="btn-secondary text-sm">
              {t('klasser.nullstillSortering')}
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="data-table data-table-compact">
          <thead>
            <tr>
              <th className="text-left max-w-[180px]">{t('common.klasse')}</th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 whitespace-nowrap" onClick={() => handleSort('count')}>
                {t('common.antall')} {<SortIcon col="count" sort={sort} />}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 whitespace-nowrap" onClick={() => handleSort('share_pct')}>
                {t('common.andelPct')} {<SortIcon col="share_pct" sort={sort} />}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 whitespace-nowrap" onClick={() => handleSort('finans_andel_pct')}>
                {t('klasser.finansiertPct')} {<SortIcon col="finans_andel_pct" sort={sort} />}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 whitespace-nowrap" onClick={() => handleSort('avg_bilalder')}>
                {t('orgSammenligning.snittBilalder')} {<SortIcon col="avg_bilalder" sort={sort} />}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 whitespace-nowrap" onClick={() => handleSort('avg_kjoper_alder')}>
                {t('klasser.snittKjoperalder')} {<SortIcon col="avg_kjoper_alder" sort={sort} />}
              </th>
              <th className="text-left w-full min-w-[160px]">
                {t('klasser.toppMerke')}
              </th>
              <th className="text-center">{t('klasser.handling')}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
              <td className="font-bold text-primary-700 dark:text-primary-300 py-2">
                <span className="text-primary-600 dark:text-primary-400 font-bold mr-1">∑</span>
                {t('klasser.total')}
              </td>
              <td className="text-right font-bold py-2">{totalCount.toLocaleString('nb-NO')}</td>
              <td className="text-right font-bold py-2">
                <span className="font-bold text-primary-600 dark:text-primary-400">100,0</span>
              </td>
              <td className="py-2"></td>
              <td className="py-2"></td>
              <td className="py-2"></td>
              <td className="py-2"></td>
              <td className="text-center py-2"><span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span></td>
            </tr>
            {sorted.map((row, idx) => (
              <tr
                key={row.class_label}
                className={`${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''} hover:bg-gray-100 dark:hover:bg-dark-border transition-colors cursor-pointer`}
                onClick={() => navigate(klasseDetaljerUrl(row.class_label, filters))}
              >
                <td className="font-bold">{row.class_label}</td>
                <td className="text-right">{row.count.toLocaleString('nb-NO')}</td>
                <td className="text-right">
                  {row.share_pct.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                </td>
                <td className="text-right">
                  {row.finans_andel_pct.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                </td>
                <td className="text-right">
                  {row.avg_bilalder != null ? t('units.aar', { value: row.avg_bilalder.toLocaleString('nb-NO', { maximumFractionDigits: 1 }) }) : '—'}
                </td>
                <td className="text-right">
                  {row.avg_kjoper_alder != null ? row.avg_kjoper_alder.toLocaleString('nb-NO', { maximumFractionDigits: 1 }) : '—'}
                </td>
                <td>{row.top_merke ?? '—'}</td>
                <td className="text-center">
                  <button
                    onClick={e => { e.stopPropagation(); navigate(klasseDetaljerUrl(row.class_label, filters)) }}
                    className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    <span className="text-xs font-medium">{t('common.seDetaljer')}</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('klasser.ingenDataFunnet')}</p>
      )}
    </div>
  )
}
